"use client"

import { useRef, type ReactNode } from "react"
import { ImagePlus, Loader2, Send, X } from "lucide-react"
import { GlassTextarea, GlassIconButton, GlassGlowButton } from "@/shared/ui/glass"
import { cn } from "@/lib/utils"
import type { PendingReplyAttachment } from "../model/use-reply-attachments"

export const MAX_REPLY_LENGTH = 5000
const COUNTER_WARN_AT = 4500

export interface FeedbackComposerProps {
  readonly value: string
  readonly onChange: (next: string) => void
  readonly onSend: () => void
  readonly isReplying: boolean
  readonly disabled?: boolean
  readonly error?: string | null
  /**
   * "icon-only" (default) keeps today's user-side ghost icon button. "primary"
   * promotes the send to a `GlassGlowButton` — the quiet-primary CTA for glass
   * surfaces (a solid brand fill would compete with surrounding glass).
   */
  readonly sendVariant?: "icon-only" | "primary"
  /** Live N/5000 character counter. Default off, so the user side is unchanged. */
  readonly showCounter?: boolean
  /** "Enter to send · Shift+Enter for a new line" hint + textarea aria-describedby. Default off. */
  readonly showKeyboardHint?: boolean
  /** Optional secondary action rendered beside the send button (e.g. "Acknowledge only"). */
  readonly secondaryAction?: ReactNode
  readonly placeholder?: string
  readonly ariaLabel?: string
  /**
   * Image attachments (optional — omitting all three keeps the composer text-only).
   * The consuming slice owns the upload state via useReplyAttachments; the composer
   * only renders the picker button + preview chips and folds them into canSend.
   */
  readonly pendingAttachments?: ReadonlyArray<PendingReplyAttachment>
  readonly onAttachFiles?: (files: ReadonlyArray<File>) => void
  readonly onRemoveAttachment?: (id: string) => void
  readonly attachmentError?: string | null
}

const HINT_ID = "feedback-composer-hint"

/**
 * Shared reply composer. Enter sends, Shift+Enter inserts a newline. The 5000-char
 * cap is client-side immediate feedback only; the server is the source of truth via
 * InvalidReplyContent. Pure presentational — the consuming slice owns the mutation.
 */
export function FeedbackComposer({
  value,
  onChange,
  onSend,
  isReplying,
  disabled,
  error,
  sendVariant = "icon-only",
  showCounter = false,
  showKeyboardHint = false,
  secondaryAction,
  placeholder,
  ariaLabel = "Send reply",
  pendingAttachments,
  onAttachFiles,
  onRemoveAttachment,
  attachmentError,
}: FeedbackComposerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const attachments = pendingAttachments ?? []
  const hasReadyAttachment = attachments.some((a) => a.status === "ready")
  const attachmentsBlocked =
    attachments.some((a) => a.status === "uploading") || attachments.some((a) => a.status === "failed")
  const tooLong = value.length > MAX_REPLY_LENGTH
  const canSend =
    (value.trim().length > 0 || hasReadyAttachment) && !tooLong && !attachmentsBlocked && !isReplying && !disabled

  return (
    <div className="space-y-1">
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {attachments.map((a) => (
            <div
              key={a.id}
              className={cn(
                "group relative h-12 w-12 overflow-hidden rounded-lg border bg-gray-50 shadow-xs",
                a.status === "failed" ? "border-red-300" : "border-black/8",
              )}
            >
              <img
                src={a.previewUrl}
                alt={a.file.name}
                className={cn("h-full w-full object-cover", a.status !== "ready" && "opacity-60")}
              />
              {a.status === "uploading" && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/40">
                  <Loader2 className="h-4 w-4 animate-spin text-[#5651ff]" />
                </div>
              )}
              {a.status === "failed" && (
                <div className="absolute inset-x-0 bottom-0 bg-red-500/90 py-px text-center text-[9px] font-medium text-white">
                  Failed
                </div>
              )}
              {onRemoveAttachment && (
                <button
                  type="button"
                  aria-label={`Remove ${a.file.name}`}
                  onClick={() => onRemoveAttachment(a.id)}
                  className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition-opacity hover:bg-black/80 group-hover:opacity-100"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex items-end gap-1.5">
        <GlassTextarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault()
              if (canSend) onSend()
            }
          }}
          placeholder={placeholder ?? (disabled ? "This conversation is no longer available" : "Write a reply...")}
          rows={1}
          autoGrowMaxPx={140}
          disabled={disabled}
          aria-describedby={showKeyboardHint ? HINT_ID : undefined}
          className="flex-1 text-sm"
        />
        {onAttachFiles && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = e.target.files ? Array.from(e.target.files) : []
                if (files.length > 0) onAttachFiles(files)
                // Reset so selecting the same file again re-fires onChange.
                e.target.value = ""
              }}
            />
            <GlassIconButton
              type="button"
              aria-label="Attach image"
              disabled={disabled || isReplying}
              onClick={() => fileInputRef.current?.click()}
            >
              <ImagePlus className="h-4 w-4" />
            </GlassIconButton>
          </>
        )}
        {sendVariant === "primary" ? (
          <GlassGlowButton type="button" aria-label={ariaLabel} disabled={!canSend} onClick={onSend}>
            <Send className="h-4 w-4" />
            Send
          </GlassGlowButton>
        ) : (
          <GlassIconButton type="button" aria-label={ariaLabel} disabled={!canSend} onClick={onSend}>
            <Send className="h-4 w-4" />
          </GlassIconButton>
        )}
        {secondaryAction}
      </div>

      {(showKeyboardHint || showCounter) && (
        <div className="flex items-center justify-between gap-2">
          {showKeyboardHint ? (
            <p id={HINT_ID} className="text-[10px] text-gray-400">
              <kbd className="font-sans font-medium">Enter</kbd> to send ·{" "}
              <kbd className="font-sans font-medium">Shift+Enter</kbd> for a new line
            </p>
          ) : (
            <span />
          )}
          {showCounter && (
            <p
              className={cn(
                "text-[10px] tabular-nums",
                tooLong ? "text-red-500" : value.length >= COUNTER_WARN_AT ? "text-amber-600" : "text-gray-400",
              )}
            >
              {value.length.toLocaleString()}/{MAX_REPLY_LENGTH.toLocaleString()}
            </p>
          )}
        </div>
      )}

      {tooLong && (
        <p className="text-[11px] text-red-500">
          Reply exceeds the {MAX_REPLY_LENGTH.toLocaleString()} character limit ({value.length.toLocaleString()}).
        </p>
      )}
      {attachmentError && <p className="text-[11px] text-red-500">{attachmentError}</p>}
      {error && <p className="text-[11px] text-red-500">{error}</p>}
    </div>
  )
}
